import { useCallback, useRef, useState } from "react";
import type { DragEvent } from "react";
import { useTransferStore, type Slot } from "../store/transferStore";

interface DropZoneProps {
  slot: Slot;
  label: string;
  prompt: string;
}

/**
 * A click-or-drag file target for one GLB slot. Reads the current slot state
 * from the store and dispatches loads through it.
 */
export function DropZone({ slot, label, prompt }: DropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [hot, setHot] = useState(false);

  const slotState = useTransferStore((s) => s[slot]);
  const loadFile = useTransferStore((s) => s.loadFile);
  const busy = useTransferStore((s) => s.busy);

  const isSet = Boolean(slotState.gltf);

  const onPick = useCallback(() => {
    if (busy) return;
    inputRef.current?.click();
  }, [busy]);

  const onFiles = useCallback(
    (files: FileList | null) => {
      const file = files?.[0];
      if (file) void loadFile(slot, file);
    },
    [loadFile, slot],
  );

  const onDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setHot(false);
      if (busy) return;
      onFiles(e.dataTransfer.files);
    },
    [busy, onFiles],
  );

  const detail = isSet
    ? slotState.clips.length
      ? `${slotState.fileName} · ${slotState.clips.length} clip(s)`
      : slotState.fileName
    : "click or drag & drop";

  return (
    <div
      className={`drop${hot ? " hot" : ""}${isSet ? " set" : ""}`}
      role="button"
      tabIndex={0}
      onClick={onPick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onPick();
        }
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setHot(true);
      }}
      onDragLeave={() => setHot(false)}
      onDrop={onDrop}
    >
      <div className="k">{label}</div>
      <div className="t">{prompt}</div>
      <div className="s">{detail}</div>
      <input
        ref={inputRef}
        type="file"
        accept=".glb,.gltf"
        hidden
        onChange={(e) => onFiles(e.target.files)}
      />
    </div>
  );
}
